import { useState, useEffect, useCallback } from "preact/hooks";
import { Home } from "./Home";
import { Landing } from "./Landing";
import { NotFound } from "./NotFound";
import { PortalForm } from "./PortalForm";
import { PortalView } from "./PortalView";
import { Login } from "./Login";
import { Verify } from "./Verify";
import { ForgotPassword } from "./ForgotPassword";
import { Account } from "./Account";
import { Analytics } from "./Analytics";
import { Pricing } from "./Pricing";
import { ErrorBoundary } from "./ErrorBoundary";
import {
  AuthProvider,
  NavigationProvider,
  useAuth,
  useNavigate,
} from "../contexts/AppContext";

// FT-AUTH: any 401 from a fetch sends the user to /login?next=<current>.
// Wrap window.fetch once at module load so every component (and the
// chat sidebar's inline fetches) benefits without threading auth state
// through. The wrapper is a no-op for 2xx/non-auth errors — handlers
// keep their existing flow.
const PUBLIC_PATHS = new Set([
  "/login",
  "/signup",
  "/verify",
  "/forgot",
  "/pricing",
  "/",
]);
function installAuthFetch() {
  const original = window.fetch.bind(window);
  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const res = await original(...args);
    // Don't redirect on the auth-probe itself, or on public SPA paths.
    const url =
      typeof args[0] === "string" ? args[0] : (args[0] as Request).url;
    if (
      res.status === 401 &&
      !url.includes("/api/auth/me") &&
      !PUBLIC_PATHS.has(window.location.pathname) &&
      !window.location.pathname.startsWith("/p/")
    ) {
      const next = encodeURIComponent(
        window.location.pathname + window.location.search,
      );
      window.location.href = `/login?next=${next}`;
    }
    return res;
  };
}
installAuthFetch();

export function App() {
  const [path, setPath] = useState(window.location.pathname);
  const [authState, setAuthState] = useState<
    | { status: "loading" }
    | { status: "anon" }
    | { status: "authed"; email: string }
  >({ status: "loading" });
  const [pendingEmail, setPendingEmail] = useState("");

  const navigate = useCallback(
    (to: string, options?: { replace?: boolean }) => {
      // Replacing the current entry lets flows like create -> edit keep Back
      // aimed at the page before /create, rather than reopening the form.
      if (options?.replace) {
        window.history.replaceState({}, "", to);
      } else {
        window.history.pushState({}, "", to);
      }
      setPath(to);
    },
    [],
  );

  useEffect(() => {
    const handler = () => setPath(window.location.pathname);
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  useEffect(() => {
    fetch("/api/auth/me")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.user?.email) {
          setAuthState({ status: "authed", email: data.user.email });
        } else {
          setAuthState({ status: "anon" });
        }
      })
      .catch(() => setAuthState({ status: "anon" }));
  }, []);

  const logout = useCallback(async () => {
    await fetch("/api/auth/logout", { method: "POST" });
    setAuthState({ status: "anon" });
    navigate("/login");
  }, [navigate]);

  const onAuthed = useCallback((email: string) => {
    setAuthState({ status: "authed", email });
  }, []);

  const goVerify = useCallback(
    (email: string) => {
      setPendingEmail(email);
      navigate("/verify" + window.location.search);
    },
    [navigate],
  );

  return (
    <NavigationProvider navigate={navigate}>
      <AuthProvider auth={authState} logout={logout}>
        <ErrorBoundary>
          <AppInner
            path={path}
            pendingEmail={pendingEmail}
            setPendingEmail={setPendingEmail}
            onAuthed={onAuthed}
            goVerify={goVerify}
          />
        </ErrorBoundary>
      </AuthProvider>
    </NavigationProvider>
  );
}

function AppInner({
  path,
  pendingEmail,
  setPendingEmail,
  onAuthed,
  goVerify,
}: {
  path: string;
  pendingEmail: string;
  setPendingEmail: (email: string) => void;
  onAuthed: (email: string) => void;
  goVerify: (email: string) => void;
}) {
  const { auth } = useAuth();
  const navigate = useNavigate();

  // Redirect to login if /verify is reached without a pending email.
  // Must be at top level (not inside the if-block) to keep hook order stable.
  useEffect(() => {
    if (path === "/verify" && !pendingEmail) {
      navigate("/login");
    }
  }, [path, pendingEmail, navigate]);

  if (path === "/login") {
    return (
      <Login
        mode="login"
        onAuthed={onAuthed}
        onVerificationRequired={goVerify}
      />
    );
  }
  if (path === "/signup") {
    return (
      <Login
        mode="signup"
        onAuthed={onAuthed}
        onVerificationRequired={goVerify}
      />
    );
  }
  if (path === "/forgot") {
    return <ForgotPassword onAuthed={onAuthed} />;
  }
  if (path === "/verify") {
    if (!pendingEmail) return null;
    return (
      <Verify
        email={pendingEmail}
        onAuthed={(email) => {
          setPendingEmail("");
          onAuthed(email);
        }}
      />
    );
  }

  if (auth.status === "loading") {
    return (
      <div class="d-flex justify-content-center align-items-center vh-100">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (auth.status === "anon") {
    if (path === "/") return <Landing />;
    if (path === "/pricing") return <Pricing />;
    // Known auth-gated paths: redirect to login with ?next so the user lands
    // back after signing in. Anything else is a 404 even for anon visitors.
    const AUTH_GATED = ["/create", "/account"];
    const isAuthGated =
      AUTH_GATED.includes(path) ||
      path.startsWith("/edit/") ||
      path.startsWith("/analytics/");
    if (isAuthGated) {
      const next = encodeURIComponent(path);
      window.location.href = `/login?next=${next}`;
      return null;
    }
    return <NotFound />;
  }

  if (path === "/") {
    return <Home />;
  }
  if (path === "/create") {
    return <PortalForm />;
  }
  if (path === "/account") {
    return <Account />;
  }
  if (path === "/pricing") {
    return <Pricing />;
  }
  if (path.startsWith("/analytics/")) {
    const id = path.slice("/analytics/".length);
    return <Analytics id={id} />;
  }
  if (path.startsWith("/edit/")) {
    const id = path.slice("/edit/".length);
    return <PortalView id={id} />;
  }
  return <NotFound />;
}
